import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const allClientLogos = [
  { src: '/client_st.png', alt: 'Staatsolie' },
  { src: '/client_semc.png', alt: 'SEMC' },
  { src: '/client_spbs.png', alt: 'SPBS' },
  { src: '/client_popeyes.png', alt: 'Popeyes' },
  { src: '/client_rosebel.png', alt: 'Rosebel Gold Mines' },
  { src: '/client_mas.png', alt: 'MAS' },
  { src: '/client_alginco.png', alt: 'Alginco' },
  { src: '/client_hakrinbank.png', alt: 'Hakrinbank' },
  { src: '/client_gow2.png', alt: 'GOW2' },
  { src: '/client_diakonesse.png', alt: 'Diakonessenhuis' },
  { src: '/client_newmont.png', alt: 'Newmont' },
  { src: '/client_mcdonalds.png', alt: 'McDonalds' },
  { src: '/client_suriname.png', alt: 'Suriname' },
  { src: '/client_vsh.png', alt: 'VSH' },
  { src: '/client_staatsolie2.png', alt: 'Staatsolie' }
];

// Fisher-Yates shuffle algorithm
const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const TrustedClients = () => {
  const { t } = useLanguage();
  const [displayLogos, setDisplayLogos] = useState([]);
  const [isPaused, setIsPaused] = useState(false);
  const [translateX, setTranslateX] = useState(0);
  const animationRef = useRef(null);
  const speedRef = useRef(1.2);
  
  // Logo dimensions
  const logoWidth = 180;
  const totalLogos = allClientLogos.length;
  const singleSetWidth = totalLogos * logoWidth;

  // Generate new randomized set of logos
  const generateRandomLogos = useCallback(() => {
    const shuffled1 = shuffleArray(allClientLogos);
    const shuffled2 = shuffleArray(allClientLogos);
    const shuffled3 = shuffleArray(allClientLogos);
    const shuffled4 = shuffleArray(allClientLogos);
    return [...shuffled1, ...shuffled2, ...shuffled3, ...shuffled4];
  }, []);

  // Initialize with random logos
  useEffect(() => {
    setDisplayLogos(generateRandomLogos());
    // Start from random position
    const randomStart = Math.floor(Math.random() * singleSetWidth);
    setTranslateX(-randomStart);
  }, [generateRandomLogos, singleSetWidth]);

  // Animation loop with re-randomization
  const animate = useCallback(() => {
    if (!isPaused) {
      setTranslateX(prev => {
        const newValue = prev - speedRef.current;
        // When we've scrolled one full set, re-randomize and reset
        if (Math.abs(newValue) >= singleSetWidth) {
          // Re-randomize the logos for the next loop
          setDisplayLogos(generateRandomLogos());
          return 0;
        }
        return newValue;
      });
    }
    animationRef.current = requestAnimationFrame(animate);
  }, [isPaused, singleSetWidth, generateRandomLogos]);

  useEffect(() => {
    animationRef.current = requestAnimationFrame(animate);
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [animate]);

  // Navigation with re-randomization
  const handleScrollLeft = () => {
    setDisplayLogos(generateRandomLogos());
    setTranslateX(0);
  };

  const handleScrollRight = () => {
    setDisplayLogos(generateRandomLogos());
    setTranslateX(-singleSetWidth / 2);
  };

  return (
    <section className="bg-white py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-10">
        <h2 className="font-montserrat font-black text-[#e21b1b] text-2xl sm:text-3xl lg:text-4xl text-center uppercase">
          {t('trustedClients.title')}
        </h2>
      </div>
      
      {/* Slider Container */}
      <div className="relative">
        {/* Left Arrow */}
        <button
          onClick={handleScrollLeft}
          className="absolute left-2 sm:left-4 lg:left-8 top-1/2 -translate-y-1/2 z-30 bg-white hover:bg-[#e21b1b] text-[#1a1a1a] hover:text-white rounded-full p-2 sm:p-3 shadow-lg transition-all duration-300 hover:scale-110 border border-gray-200"
          aria-label="Shuffle and scroll left"
          data-testid="slider-arrow-left"
        >
          <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6" />
        </button>

        {/* Right Arrow */}
        <button
          onClick={handleScrollRight}
          className="absolute right-2 sm:right-4 lg:right-8 top-1/2 -translate-y-1/2 z-30 bg-white hover:bg-[#e21b1b] text-[#1a1a1a] hover:text-white rounded-full p-2 sm:p-3 shadow-lg transition-all duration-300 hover:scale-110 border border-gray-200"
          aria-label="Shuffle and scroll right"
          data-testid="slider-arrow-right"
        >
          <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6" />
        </button>

        {/* Slider viewport */}
        <div className="relative overflow-hidden mx-12 sm:mx-16 lg:mx-20">
          {/* Left fade gradient */}
          <div 
            className="absolute left-0 top-0 bottom-0 w-16 sm:w-24 lg:w-32 z-20 pointer-events-none"
            style={{ background: 'linear-gradient(to right, rgb(255,255,255) 0%, rgb(255,255,255) 30%, rgba(255,255,255,0) 100%)' }}
          />
          
          {/* Right fade gradient */}
          <div 
            className="absolute right-0 top-0 bottom-0 w-16 sm:w-24 lg:w-32 z-20 pointer-events-none"
            style={{ background: 'linear-gradient(to left, rgb(255,255,255) 0%, rgb(255,255,255) 30%, rgba(255,255,255,0) 100%)' }}
          />
          
          {/* Scrolling track */}
          <div 
            className="flex py-4"
            style={{ 
              transform: `translateX(${translateX}px)`
            }}
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
          >
            {displayLogos.map((logo, index) => (
              <div
                key={`${logo.alt}-${index}`}
                className="flex-shrink-0 flex items-center justify-center"
                style={{ width: '180px', padding: '0 20px' }}
              >
                <img
                  src={logo.src}
                  alt={logo.alt}
                  className="h-20 w-auto object-contain hover:scale-110 transition-transform duration-300"
                  loading="lazy"
                  style={{ maxWidth: '140px' }}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TrustedClients;
